import { useEffect, useState } from 'react';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { Field } from '../../../../components/field/field';
import { Toggle } from '../../../../components/form/toggle';
import { SelectForm } from '../../../../components/select-form/select-form';
import { Typography } from '../../../../components/typography/typography';
import { Button } from '../../../../components/form/button';
import { Spinner } from '../../../../components/spinner/spinner';
import { TextArea } from '../../../../components/textarea/textarea';
import {
  ProcessingFeeOverride,
  ProcessingFeeOverrideConf,
  TagPricing,
  User,
  UserInternalNote,
  UserInternalNoteRequest,
  UserType,
} from '../../../../clients/quipli-service/user/user';
import { useAdmin } from '../../../../hooks/use-admin-context';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { ApiError } from '../../../../clients/axios-instance';
import Icon from '../../../../components/icons/icon';
import { ConfirmModal } from '../../../../components/modal/confirm-modal';
import { Dropdown } from '../../../../components/dropdown/dropdown';
import { CreatableMultiselectInput } from '../../components/user-secondary-emails-field';
import { toast } from '../../../../util/toasts';
import { Radio } from '../../../../components/radio/radio';
import { Card } from '../../../../components/card/card';
import Dinero, { Currency } from 'dinero.js';
import { Checkbox } from '../../../../components/checkbox/checkbox';
import { Constants } from '../../../../constants/constants';
import { Phone } from '../../../../components/field/phone';
import { useAdminApiV1 } from '../../../../clients/quipli-service/admin/admin-api-v1';
import { Admin } from '../../../../clients/quipli-service/admin/admin';
import { TaxCodeSelect } from '../../../tax-setup/tax-code-select';

export const General = ({ user, setUser }: { user: User; setUser: (user: User) => void }) => {
  const { updateUser, listTagPricings, listUserInternalNotes, createUserInternalNote, getMultipleConfigurations } =
      useAPI(),
    admin = useAdmin();
  const { getAdminsList } = useAdminApiV1();

  const [userState, setUserState] = useState<{
    id: number;
    name: string;
    email: string;
    secondaryEmails?: string[];
    company: string;
    phone: string;
    chargeTax: boolean;
    doNoRent: boolean;
    paymentBan: boolean;
    requirePoNum: boolean;
    requireCompanyRep: boolean;
    tagPricing?: TagPricing | null;
    type: UserType;
    creditLimit?: number;
    structureType: 'user' | 'company';
    processingFeeOverride?: ProcessingFeeOverride;
    defaultSalesRepId?: number;
    defaultTaxCode?: string;
  }>({ ...user, creditLimit: user.creditLimit?.getAmount(), processingFeeOverride: user.processingFeeOverride });

  const [isSaving, setIsSaving] = useState(false),
    [listTags, setListTags] = useState<TagPricing[]>([]),
    [listInternalNotes, setlistInternalNotes] = useState<UserInternalNote[]>([]),
    [note, setNote] = useState<string>(''),
    [isNewNoteLoading, setNewNoteLoading] = useState<boolean>(false),
    [newPassword, setNewPassword] = useState<string>(''),
    [confirmNewPassword, setConfirmNewPassword] = useState<string>(''),
    [confEnableDoNotRent, setConfEnableDoNotRent] = useState<boolean>(false),
    [confRenterTagPricing, setRenterTagPricing] = useState<boolean>(false),
    [confCreditLimitEnabled, setCreditLimitEnabled] = useState<boolean>(false),
    [confCurrency, setConfCurrency] = useState<string>('USD'),
    [confDefaultUserTaxCode, setConfDefaultUserTaxCode] = useState<boolean>(false),
    [processingFeeOverrideConf, setProcessingFeeOverrideConf] = useState<ProcessingFeeOverrideConf>(),
    [phone, setPhone] = useState<string>(user.phone),
    [phoneIsValid, setPhoneIsValid] = useState<boolean>(true),
    [commissionEnabled, setCommissionEnabled] = useState<boolean>(false),
    [admins, setAdmins] = useState<Admin[]>([]);

  const init = async () => {
    Promise.all([
      listTagPricings(true),
      listUserInternalNotes(user.id),
      getMultipleConfigurations([
        'CONF_ENABLE_DO_NOT_RENT',
        'CONF_RENTER_TAG_PRICING',
        'CONF_CREDIT_LIMIT',
        'CONF_CURRENCY',
        'CONF_PROCESSING_FEE_OVERRIDE',
        'CONF_COMMISSION_ENABLED',
        'CONF_DEFAULT_USER_TAX_CODE',
      ]),
      getAdminsList(),
    ]).then(([tagPricingList, internalNotesList, configs, _admins]) => {
      setListTags(tagPricingList);
      setConfEnableDoNotRent(configs.find((c) => c.variable === 'CONF_ENABLE_DO_NOT_RENT')?.value === '1');
      setlistInternalNotes(internalNotesList);
      setRenterTagPricing(configs.find((c) => c.variable === 'CONF_RENTER_TAG_PRICING')?.value === '1');
      setCreditLimitEnabled(configs.find((c) => c.variable === 'CONF_CREDIT_LIMIT')?.value === '1');
      setConfCurrency(configs.find((c) => c.variable === 'CONF_CURRENCY')?.value ?? 'USD');
      setConfDefaultUserTaxCode(configs.find((c) => c.variable === 'CONF_DEFAULT_USER_TAX_CODE')?.value === '1');
      setProcessingFeeOverrideConf(
        configs.find((c) => c.variable == 'CONF_PROCESSING_FEE_OVERRIDE')?.value as ProcessingFeeOverrideConf,
      );
      setCommissionEnabled(configs.find((c) => c.variable == 'CONF_COMMISSION_ENABLED')?.value === '1');
      setAdmins(_admins);
    });
  };

  const deleteInternalNote = (id: number) => {
    setlistInternalNotes(listInternalNotes.filter((item) => item.id !== id));
  };

  const updateGeneralInfo = () => {
    if (phone !== '' && !phoneIsValid) {
      toast('Please enter a valid phone number', 'error');
      return;
    }

    setIsSaving(true);

    const body = {
      ...user,
      name: userState.name,
      email: userState.email,
      secondaryEmails: userState.secondaryEmails,
      company: userState.company,
      phone: phone,
      chargeTax: userState.chargeTax,
      requirePoNum: userState.requirePoNum,
      requireCompanyRep: userState.requireCompanyRep,
      doNoRent: userState.doNoRent,
      paymentBan: userState.paymentBan,
      tagPricing: userState.tagPricing ?? undefined,
      newPassword: newPassword !== '' && newPassword === confirmNewPassword ? newPassword : null,
      creditLimit: userState.creditLimit
        ? Dinero({ amount: userState.creditLimit, currency: confCurrency as Currency })
        : undefined,
      structureType: userState.structureType,
      processingFeeOverride: userState.processingFeeOverride,
      defaultSalesRepId: userState.defaultSalesRepId,
      defaultTaxCode: userState.defaultTaxCode,
    };

    updateUser(body)
      .then((user) => {
        setUser(user);
        toast('User details have been updated successfully', 'success');
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error updating user details';
        toast(errMsg, 'error');
      })
      .finally(() => setIsSaving(false));
  };

  const addNote = async () => {
    if (!note.length) return;

    setNewNoteLoading(true);

    const body = {
      userId: user.id,
      comment: note,
      authorId: admin.id,
      edited: false,
    };

    const result = await createUserInternalNote(user.id, body as UserInternalNoteRequest);

    setlistInternalNotes((prevState) => {
      setNote('');
      setNewNoteLoading(false);
      return [...prevState, result];
    });
  };

  // Hooks
  useEffect(() => {
    init();
  }, []);

  const CustomerRadioSelect = (props: { field: keyof typeof userState; label: string }) => {
    const { field, label } = props;

    return (
      <div className="user-setup-field">
        <div className="mb-1">
          <span className="font-sans text-sm font-semibold text-gray-500">{label}</span>
        </div>
        <div className="flex gap-x-6">
          <div className="flex gap-x-1 items-center">
            <Radio
              name={field}
              label="No"
              isChecked={!userState[field]}
              onChange={(isChecked) => isChecked && setUserState({ ...userState, [field]: false })}
            />
          </div>
          <div className="flex gap-x-1 items-center">
            <Radio
              name={field}
              isChecked={userState[field] as boolean}
              onChange={(isChecked) => isChecked && setUserState({ ...userState, [field]: true })}
              label="Yes"
            />
          </div>
        </div>
      </div>
    );
  };

  return userState ? (
    <div className="tab-section">
      <Card style={{ minWidth: '50%' }} className="flex flex-column w-fit p-4 gap-4">
        {user.structureType == 'user' && (
          <div className="user-setup-field">
            <div className="mb-1">
              <span className="font-sans text-sm font-semibold text-gray-500">Switch to Company</span>
            </div>
            <Toggle
              onClick={() => {
                setUserState({ ...userState, structureType: 'company' });
              }}
              checked={userState.structureType === 'company'}
            />
          </div>
        )}
        <Field
          className="user-setup-field"
          type="text"
          label="Email address"
          value={userState.email}
          onChange={(email: string) => {
            setUserState({ ...userState, email });
          }}
          isRequired={true}
        />
        <CreatableMultiselectInput
          className="user-setup-field"
          emails={user.secondaryEmails}
          onChange={(secondaryEmails: string[]) => {
            setUserState({ ...userState, secondaryEmails });
          }}
          label="Secondary email addresses"
          caption="Add emails to receive order invoices and updates pertaining to this account"
          placeholder="Type email and press enter..."
        />
        <Field
          className="user-setup-field"
          type="text"
          label={user.structureType == 'user' ? 'Customer name' : 'Company name'}
          value={userState.name}
          onChange={(name: string) => {
            setUserState({ ...userState, name });
          }}
          isRequired={true}
        />
        {user.structureType == 'user' && (
          <Field
            className="user-setup-field"
            type="text"
            label="Company name"
            value={userState.company}
            onChange={(company: string) => {
              setUserState({ ...userState, company });
            }}
            isRequired={false}
          />
        )}

        <Phone
          className="user-setup-field"
          label="Phone number"
          value={phone}
          onChange={setPhone}
          onChangeValidity={setPhoneIsValid}
        />

        {confCreditLimitEnabled && (
          <Field
            className="user-setup-field"
            type="number"
            label="Credit limit"
            value={userState.creditLimit}
            onChange={(creditLimit?: number) => {
              setUserState({
                ...userState,
                creditLimit,
              });
            }}
            isRequired={false}
            disabled={!admin.hasPermissions(Constants.Permissions.Credit_Limit_Adjustment)}
          />
        )}
        {user.type === UserType.Registered ? (
          <>
            <Field
              data-password="check-password"
              className={`user-setup-field ${
                newPassword !== confirmNewPassword ? 'border border-solid border-red-900' : ''
              }`}
              type="password"
              label="Password"
              value={newPassword}
              placeholder="*********"
              onChange={(password: string) => {
                setNewPassword(password);
              }}
              autoComplete="off"
            />
            <Field
              className={`user-setup-field ${
                newPassword !== confirmNewPassword ? 'border border-solid border-red-900' : ''
              }`}
              type="password"
              label="Confirm password"
              value={confirmNewPassword}
              onChange={(confirmPassword: string) => {
                setConfirmNewPassword(confirmPassword);
              }}
              autoComplete="off"
            />
          </>
        ) : (
          ''
        )}

        <CustomerRadioSelect field="chargeTax" label="Charge rental tax" />
        {confDefaultUserTaxCode && (
          <TaxCodeSelect
            onChange={(tc) =>
              setUserState((u) => {
                return { ...u, defaultTaxCode: tc };
              })
            }
            value={userState.defaultTaxCode}
            label={'Default Tax Code'}
          />
        )}
        <CustomerRadioSelect field="requirePoNum" label="Require PO Number" />
        {user.structureType == 'company' && (
          <CustomerRadioSelect field="requireCompanyRep" label="Require Company Representative" />
        )}

        {processingFeeOverrideConf !== ProcessingFeeOverrideConf.ApplyFee && (
          <div className="user-setup-field">
            <div className="mb-1">
              <span className="font-sans text-sm font-semibold text-gray-500">
                {processingFeeOverrideConf == ProcessingFeeOverrideConf.OptionallyApplyFee
                  ? 'Waive Processing Fee'
                  : 'Apply Processing Fee'}
              </span>
            </div>
            <div className="flex gap-x-6">
              <div className="flex gap-x-1 items-center">
                <Checkbox
                  name="processing_fee_override"
                  isChecked={
                    !userState.processingFeeOverride
                      ? false
                      : processingFeeOverrideConf === ProcessingFeeOverrideConf.OptionallyApplyFee
                      ? userState.processingFeeOverride == ProcessingFeeOverride.WaiveFee
                      : userState.processingFeeOverride == ProcessingFeeOverride.ApplyFee
                  }
                  onChange={(isChecked) =>
                    isChecked
                      ? setUserState({
                          ...userState,
                          processingFeeOverride:
                            processingFeeOverrideConf === ProcessingFeeOverrideConf.OptionallyApplyFee
                              ? ProcessingFeeOverride.WaiveFee
                              : ProcessingFeeOverride.ApplyFee,
                        })
                      : setUserState({ ...userState, processingFeeOverride: undefined })
                  }
                  label="Yes"
                />
              </div>
            </div>
          </div>
        )}

        {confEnableDoNotRent ? (
          <div className="user-setup-field">
            <div className="mb-1">
              <span className="font-sans text-sm font-semibold text-gray-500">Do not rent</span>
            </div>
            <Toggle
              onClick={() => {
                setUserState({ ...userState, doNoRent: !userState.doNoRent });
              }}
              checked={userState.doNoRent}
            />
            {userState.doNoRent && (
              <div className="danger_box">
                <Icon icon="InfoCircle" size="medium" stroke="var(--error-500)" />
                <p className="font-sans text-sm text-error-500">
                  Turning on this feature will block the customer from submitting rental orders online.
                </p>
              </div>
            )}
          </div>
        ) : (
          ''
        )}

        <div className="user-setup-field">
          <div className="mb-1">
            <span className="font-sans text-sm font-semibold text-gray-500">Prohibit Payments</span>
          </div>
          <Toggle
            onClick={() => {
              setUserState({ ...userState, paymentBan: !userState.paymentBan });
            }}
            checked={userState.paymentBan}
          />
          {userState.paymentBan && (
            <div className="danger_box">
              <Icon icon="InfoCircle" size="medium" stroke="var(--error-500)" />
              <p className="font-sans text-sm text-error-500">
                Turning this on will prevent the user from making payments.
              </p>
            </div>
          )}
        </div>

        {confRenterTagPricing ? (
          <div className="user-setup-field">
            <div className="mb-1">
              <span className="font-sans text-sm font-semibold text-gray-500">Tag pricing</span>
            </div>
            <SelectForm
              options={listTags}
              value={userState.tagPricing}
              onChange={(tag) => {
                setUserState({ ...userState, tagPricing: tag });
              }}
              getOptionLabel={(o) => o.name}
              getOptionValue={(o) => `${o.id}`}
              isClearable={true}
            />
          </div>
        ) : (
          ''
        )}

        {commissionEnabled && (
          <div className="user-setup-field">
            <div className="mb-1">
              <span className="font-sans text-sm font-semibold text-gray-500">Sales Representative</span>
            </div>
            <SelectForm
              options={admins}
              value={admins.find((a) => a.adminId == userState.defaultSalesRepId)}
              onChange={(v) => {
                setUserState({ ...userState, defaultSalesRepId: v?.adminId });
              }}
              getOptionLabel={(o) => o.adminFullName}
              isClearable={true}
            />
          </div>
        )}

        <div className="user-setup-field flex justify-end">
          {isSaving ? (
            <div className="w-full flex justify-center">
              <Spinner />
            </div>
          ) : (
            <button
              className="save-button cursor-pointer rounded-md px-4 py-2 text-black shadow-sm"
              onClick={updateGeneralInfo}
              disabled={isSaving}
            >
              Save Changes
            </button>
          )}
        </div>
      </Card>

      <div className="flex justify-between items-center mt-10 mb-5">
        <Typography type="text-xl" weight="semi-bold">
          Internal customer notes
        </Typography>
      </div>
      <div className="card">
        <div className="activity-timeline">
          {listInternalNotes.map((item, index) => (
            <InternalNoteItem key={`internalNote-item-${index}`} object={item} onDelete={deleteInternalNote} />
          ))}
        </div>

        <div className="new-note-section">
          <div className="flex-grow form-input note-shadow !p-[14px]">
            <TextArea
              value={note}
              onChange={({ target }) => setNote(target.value)}
              rows={3}
              className="border-none w-full font-sans text-sm"
              placeholder="Leave a note..."
            />

            <div className=" flex justify-end items-center">
              {isNewNoteLoading ? (
                <div className="w-full flex justify-center">
                  <Spinner />
                </div>
              ) : (
                <>
                  <div className="flex-shrink-0">
                    <Button className="button-secondary" onClick={addNote} disabled={!note.length}>
                      <Typography className="text-grey-800" type="text-sm" weight="semi-bold">
                        Add Note
                      </Typography>
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <Spinner />
  );
};

const InternalNoteItem = ({ object, onDelete }: { object: UserInternalNote; onDelete: (id: number) => void }) => {
  const [isModalDeleteOpen, setModalDeleteOpen] = useState<boolean>(false);
  const { deleteUserInternalNote } = useAPI();

  const removeItem = async () => {
    if (!object) return;

    await deleteUserInternalNote(object.userId, object.id);
    setModalDeleteOpen(false);
    onDelete(object.id);
  };

  return (
    <>
      <div className="timeline-item pb-6">
        <div className="item-content flex-grow">
          <div className={classNames('w-full', { 'attachment-note': !!object })}>
            <div className="flex items-center justify-between">
              <div className="label-and-date">
                <Typography type="text-sm" className="text-grey-500">
                  {object.author?.adminFullName} created a note{' '}
                  {DateTime.fromISO(object.created, { setZone: true }).toFormat('ff')}
                </Typography>
              </div>
              <div>
                <Dropdown
                  trigger={() => (
                    <Button className="button-tertiary" data-cy={`customer_menu`}>
                      <Icon icon="DotsHorizontal" stroke="var(--grey-800)" size="small" />
                    </Button>
                  )}
                >
                  <div>
                    <div
                      className="flex gap-2"
                      onClick={() => {
                        setModalDeleteOpen(true);
                      }}
                      data-cy="delete_customer"
                    >
                      <Typography type="text-sm" className="text-error-500">
                        Delete note
                      </Typography>
                    </div>
                  </div>
                </Dropdown>
              </div>
            </div>

            {object && (
              <>
                {object.comment && (
                  <div className="new-note-section">
                    <div className="flex-grow form-input note-shadow !p-[14px]">
                      <Typography type="text-sm" className="text-grey-900 whitespace-pre-line">
                        {object.comment}
                      </Typography>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={isModalDeleteOpen}
        close={() => setModalDeleteOpen(false)}
        onConfirm={removeItem}
        headerIcon={
          <div className="flex justify-center">
            <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
          </div>
        }
        prompt="Remove item?"
        additionalText="This cannot be undone"
        noLabel="Cancel"
        yesLabel="Remove"
      />
    </>
  );
};
